/**
 * ⟦celda-fecha-hora⟧ `R4-UI0` · **LA FECHA ARRIBA Y LA HORA ABAJO, EN HORA DE
 * CHILE.**
 *
 * R4-F1 regla 18 (PRD_R4.md:991): «La Fecha y hora se ve con la fecha arriba y
 * la hora abajo, en la tabla de casos y en la tabla del panel «Ver
 * conversación»». R4-F2 (PRD_R4.md:1227): «… en hora de Chile, sin importar la
 * zona horaria del navegador».
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔ NO HAY UNA SEGUNDA FUENTE DEL FORMATO, NI LA HORA DEL NAVEGADOR.
 * ════════════════════════════════════════════════════════════════════════════
 *
 * La salida cómoda es partir la hora con `toLocaleTimeString()` (y la fecha con
 * `toLocaleDateString()`) sin `timeZone`: la hora saldría del navegador, y
 * F3-CA-28 la mide con el navegador en UTC, así que la celda mostraría un día y
 * una hora corridos. Acá las dos líneas salen de `formatearFecha`
 * (`formato-fecha.ts`), que fija `timeZone: "America/Santiago"` y el formato
 * `dd-mm-aaaa hh:mm` una sola vez para toda la consola.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⟦rastro · `ADR-0082` · `[5/10/2026]`⟧ ⭐⭐ **ACEPTA `Date | string`, Y EL
 * `string` ES LA REALIDAD DEL CABLE — NO UNA COMODIDAD**
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Acá el parámetro era ~~`fecha: Date`~~ y nada más. **Esa firma hacía la
 * primitiva inusable para la pantalla de Exportación**, que es la primera que la
 * integró, y la medición es ésta:
 *
 *   · `contracts/analytics/index.ts:1452` declara
 *     `readonly sinInteraccionFechaHora: string` (y las otras tres igual);
 *   · quien produce ese `string` es **Postgres**, ya formateado:
 *     `modules/analytics/src/exportacion-de-participantes/consulta.ts:163`
 *     `FORMATO_DE_LA_LISTA = "DD-MM-YYYY HH24:MI"`, aplicado con
 *     `to_char(… AT TIME ZONE 'America/Santiago', …)` (`:554`).
 *     La celda llega como `"21-09-2026 10:15"`, **ya en hora de Chile**.
 *
 * ⛔ **Las dos maneras de volver ese texto a un `Date` son defectos**, y por eso
 * la primitiva se ensancha en vez de pedirle al consumidor que convierta:
 *   · `new Date("21-09-2026 10:15")` es **Invalid Date** —ningún motor parsea ese
 *     formato— y `formatearFecha` lo dibujaría como «fecha inválida» en las cuatro
 *     columnas de todas las filas;
 *   · reordenar a `new Date("2026-09-21T10:15")` se interpreta en la zona **DEL
 *     NAVEGADOR**, y `formatearFecha` lo vuelve a convertir a Chile: con el
 *     navegador en UTC la hora sale corrida, que es **exactamente** el defecto que
 *     el bloque de arriba existe para cerrar.
 *
 * ⇒ con un `string` **no se formatea nada**: no se lee ningún reloj, no se nombra
 * ninguna zona horaria y no se construye ningún `Date`. La zona y el formato los
 * eligió Postgres, UNA vez, para la tabla y para el CSV — que es la misma
 * propiedad que este archivo defiende para el camino `Date`.
 *
 * ⛔⛔ **EL CAMINO `Date` NO CAMBIÓ UNA LETRA**, y es la condición de este
 * ensanche: lo usan las pantallas de `R4-F1`/`R4-F4`, que reciben instantes. Lo
 * sostiene `pruebas/r4-ui0-primitivas.test.tsx`, cuyos dos casos de la celda
 * —«la fecha arriba y la hora abajo, separadas por un `<br>`» y
 * «⛔ @negative · NO usa la hora del navegador»— **no se tocaron**.
 *
 * ⚠️ **POR QUÉ UNA SOLA PRIMITIVA Y NO DOS.** La alternativa que se escribió
 * primero fue un `CeldaDeFechaYHoraDelCable` propio en
 * `src/exportacion/`: andaba, pero es **la segunda definición de «fecha arriba,
 * hora abajo» que `ADR-0386` prohíbe** —una primitiva, un sitio—, y el día que
 * alguien corrija el interlineado o el `<br>` acá, la otra copia no se enteraría.
 * El dueño lo decidió al revisar `R4-UIE1`: se ensancha la primitiva y la copia se
 * retira. **`R4-UIM1` necesita esta misma celda para sus fechas de periodo, que
 * también llegan como texto del cable**: por eso el `string` entra acá y no en una
 * pantalla.
 *
 * ⚠️ **LA CADENA VACÍA ES UN VALOR LEGÍTIMO Y SIGNIFICA ALGO.**
 * `contracts/analytics` lo declara (⟦el-guion-es-un-estado-no-un-texto⟧): la fecha
 * ausente es `""`, y `PRD_R4.md:1216` fija que queda **vacía** cuando el estado
 * dice «Sí» o «—». ⇒ con `""` esta celda **no dibuja nada** —ni el `<span>`— en vez
 * de dibujar una caja vacía o un «—» inventado: el «—» es de la columna de ESTADO,
 * no de la de fecha. Un `Date` nunca puede ser `""`, así que el camino `Date`
 * tampoco se ve afectado por esta rama.
 */

import type { JSX } from "react"
import { formatearFecha } from "./formato-fecha"

interface Props {
  /**
   * El instante, o la celda **ya formateada** que manda el cable
   * (`"dd-mm-aaaa hh:mm"`, o `""` cuando la columna no aplica).
   *
   * ⛔ No hay un tercer caso: si llega un `string`, es porque el servidor ya
   * decidió la zona y el formato. Ver ⟦rastro⟧ del encabezado.
   */
  readonly fecha: Date | string
}

export function CeldaFechaHora({ fecha }: Props): JSX.Element | null {
  /*
   * ⛔ El `typeof` es la ÚNICA bifurcación: con `Date` se formatea (hora de
   * Chile, una sola fuente); con `string` se toma tal cual, porque ya viene
   * formateado y volverlo a interpretar es el defecto del ⟦rastro⟧.
   */
  const texto = typeof fecha === "string" ? fecha : formatearFecha(fecha)

  // La ausencia declarada del cable: no se dibuja nada. Ver ⟦rastro⟧.
  if (texto === "") return null

  /*
   * `formatearFecha` devuelve `dd-mm-aaaa hh:mm` y el cable manda la misma forma:
   * se parte en el PRIMER espacio para poner la fecha arriba y la hora abajo, en
   * la misma celda.
   *
   * ⚠️ `indexOf` y no `split(" ")[1]`: con `split`, una hora que trajera segundos
   * separados por espacio perdería el resto en silencio. Un texto SIN espacio
   * —que sólo puede llegar por el cable— se dibuja entero en la primera línea en
   * vez de esconderse: un texto inesperado que se ve es un texto que alguien puede
   * reportar. ⛔ Para el camino `Date` las dos formas dan lo MISMO (`formatearFecha`
   * produce exactamente un espacio, y «fecha inválida» también), así que esto no
   * cambia ninguna aserción existente.
   */
  const corte = texto.indexOf(" ")
  const dia = corte === -1 ? texto : texto.slice(0, corte)
  const hora = corte === -1 ? "" : texto.slice(corte + 1)

  return (
    <span className="celda-fecha-hora">
      {dia}
      <br />
      {hora}
    </span>
  )
}
